/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { LoadingOverlay } from '@mantine/core';
import { RoleGuard } from './guards/RoleGuard';
import { AdminLayout } from './layouts/AdminLayout';
import { ExamLayout } from './layouts/ExamLayout';

const AdminLogin = lazy(() => import('@/pages/auth/AdminLogin'));
const Registrations = lazy(() => import('@/pages/admin/Registrations'));
const AssessmentsOverview = lazy(() => import('@/pages/admin/AssessmentsOverview'));
const Users = lazy(() => import('@/pages/admin/Users'));
const ExamSettings = lazy(() => import('@/pages/admin/ExamSettings'));
const Results = lazy(() => import('@/pages/admin/Results'));
const QuizStats = lazy(() => import('@/pages/admin/QuizStats'));
const ExamOperations = lazy(() => import('@/pages/admin/ExamOperations'));
const Malpractice = lazy(() => import('@/pages/admin/Malpractice'));
const Settings = lazy(() => import('@/pages/admin/Settings'));
const Integration = lazy(() => import('@/pages/admin/Integration'));
const ExternalExams = lazy(() => import('@/pages/admin/ExternalExams'));
const QuestionReview = lazy(() => import('@/pages/admin/QuestionReview'));
const QuestionReviewSession = lazy(() => import('@/pages/admin/QuestionReviewSession'));
const Question = lazy(() => import('@/pages/admin/Question'));
const AppManagement = lazy(() => import('@/pages/admin/AppManagement'));
const AuditLogs = lazy(() => import('@/pages/admin/AuditLogs'));
const Venues = lazy(() => import('@/pages/admin/Venues'));
const LocationsTab = lazy(() => import('@/pages/admin/venues/LocationsTab'));
const SessionsTab = lazy(() => import('@/pages/admin/venues/SessionsTab'));
const VenueCheckIns = lazy(() => import('@/pages/admin/VenueCheckIns'));
const StudentLogin = lazy(() => import('@/pages/exam/StudentLogin'));
const Instructions = lazy(() => import('@/pages/exam/Instructions'));
const ExamMode = lazy(() => import('@/pages/exam/ExamMode'));
const EndExam = lazy(() => import('@/pages/exam/EndExam'));
const ExamResult = lazy(() => import('@/pages/exam/ExamResult'));
const ExternalGrading = lazy(() => import('@/pages/external/ExternalGrading'));

function PageLoader() {
  return <LoadingOverlay visible zIndex={1000} />;
}

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/exam" replace />,
  },
  {
    path: '/admin',
    element: (
      <LazyPage>
        <AdminLogin />
      </LazyPage>
    ),
  },
  {
    path: '/external-grade/:invitationToken',
    element: (
      <LazyPage>
        <ExternalGrading />
      </LazyPage>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <RoleGuard
        allowedRoles={[
          'ADMIN',
          'BIOMETRIC',
          'QUESTION_MANAGEMENT',
          'LOCATION_MANAGER',
          'EXAM_OFFICER',
          'TECHNICAL_SUPPORT_OFFICER',
        ]}
      >
        <AdminLayout />
      </RoleGuard>
    ),
    children: [
      {
        path: 'exam-operations',
        element: <RoleGuard allowedRoles={['ADMIN', 'EXAM_OFFICER', 'TECHNICAL_SUPPORT_OFFICER']}><LazyPage><ExamOperations /></LazyPage></RoleGuard>,
      },
      {
        index: true,
        element: (
          <LazyPage>
            <AssessmentsOverview />
          </LazyPage>
        ),
      },
      {
        path: 'exam-settings',
        element: (
          <LazyPage>
            <ExamSettings />
          </LazyPage>
        ),
      },
      {
        path: 'question',
        element: (
          <LazyPage>
            <Question />
          </LazyPage>
        ),
      },
      {
        path: 'users',
        element: (
          <LazyPage>
            <Users />
          </LazyPage>
        ),
      },
      {
        path: 'result',
        element: (
          <LazyPage>
            <Results />
          </LazyPage>
        ),
      },
      {
        path: 'quiz-stats',
        element: (
          <LazyPage>
            <QuizStats />
          </LazyPage>
        ),
      },
      {
        path: 'registrations',
        element: (
          <LazyPage>
            <Registrations />
          </LazyPage>
        ),
      },
      {
        path: 'malpractice',
        element: (
          <LazyPage>
            <Malpractice />
          </LazyPage>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyPage>
            <Settings />
          </LazyPage>
        ),
      },
      {
        path: 'integration',
        element: (
          <LazyPage>
            <Integration />
          </LazyPage>
        ),
      },
      {
        path: 'question-review',
        element: (
          <LazyPage>
            <QuestionReview />
          </LazyPage>
        ),
      },
      {
        path: 'question-review/:sessionId',
        element: (
          <LazyPage>
            <QuestionReviewSession />
          </LazyPage>
        ),
      },
      {
        path: 'external-exams',
        element: (
          <LazyPage>
            <ExternalExams />
          </LazyPage>
        ),
      },
      {
        path: 'app-management',
        element: (
          <RoleGuard allowedRoles={['ADMIN']}>
            <LazyPage>
              <AppManagement />
            </LazyPage>
          </RoleGuard>
        ),
      },
      {
        path: 'audit-logs',
        element: (
          <RoleGuard allowedRoles={['ADMIN']}>
            <LazyPage>
              <AuditLogs />
            </LazyPage>
          </RoleGuard>
        ),
      },
      {
        path: 'venues',
        element: (
          <RoleGuard allowedRoles={['ADMIN', 'LOCATION_MANAGER']}>
            <LazyPage>
              <Venues />
            </LazyPage>
          </RoleGuard>
        ),
        children: [
          { index: true, element: <Navigate to="locations" replace /> },
          {
            path: 'locations',
            element: (
              <LazyPage>
                <LocationsTab />
              </LazyPage>
            ),
          },
          {
            path: 'sessions',
            element: (
              <LazyPage>
                <SessionsTab />
              </LazyPage>
            ),
          },
        ],
      },
      {
        path: 'venues/sessions/:id/check-ins',
        element: (
          <RoleGuard allowedRoles={['ADMIN', 'LOCATION_MANAGER']}>
            <LazyPage>
              <VenueCheckIns />
            </LazyPage>
          </RoleGuard>
        ),
      },
    ],
  },
  {
    path: '/exam',
    element: <ExamLayout />,
    children: [
      {
        index: true,
        element: (
          <LazyPage>
            <StudentLogin />
          </LazyPage>
        ),
      },
      {
        path: 'instruction',
        element: (
          <LazyPage>
            <Instructions />
          </LazyPage>
        ),
      },
      {
        path: 'start',
        element: (
          <LazyPage>
            <ExamMode />
          </LazyPage>
        ),
      },
      {
        path: 'result',
        element: (
          <LazyPage>
            <ExamResult />
          </LazyPage>
        ),
      },
      {
        path: 'end',
        element: (
          <LazyPage>
            <EndExam />
          </LazyPage>
        ),
      },
    ],
  },
]);
